import type { DeviceManifest, ManifestPlaylistItem, ManifestAsset } from '../types';

export interface ActivePlaylist {
  id: string;
  items: ManifestPlaylistItem[];
  assets: Map<string, ManifestAsset>;
  // Playback mode forwarded from the source ManifestPlaylist. Defaults to
  // 'sequential' when missing so older manifests stay backwards-compatible.
  mode: 'sequential' | 'shuffle' | 'weighted';
}

export interface PowerScheduleStatus {
  shouldBeOn: boolean;
  isWithinWorkingHours: boolean;
  nextPowerChange: Date | null;
  reason: 'power_off' | 'outside_working_hours' | 'active' | 'no_schedule';
}

export interface ScheduleStatus {
  inSchedule: boolean;
  activePlaylistId: string | null;
  currentRule: {
    ruleId?: string;
    startTime: string;
    endTime: string;
    days: number[];
  } | null;
}

class Scheduler {
  private manifest: DeviceManifest | null = null;
  private timezone: string = 'UTC';
  private powerScheduleCheckInterval: ReturnType<typeof setInterval> | null = null;
  private onPowerScheduleChange: ((status: PowerScheduleStatus) => void) | null = null;
  private scheduleCheckInterval: ReturnType<typeof setInterval> | null = null;
  private onScheduleChange: ((status: ScheduleStatus) => void) | null = null;
  private lastScheduleStatus: ScheduleStatus | null = null;

  setManifest(manifest: DeviceManifest): void {
    this.manifest = manifest;
    // Get timezone from power schedule or schedule rules
    if (manifest.powerSchedule?.timezone) {
      this.timezone = manifest.powerSchedule.timezone;
    } else if (manifest.schedule?.rules?.length > 0) {
      this.timezone = manifest.schedule.rules[0].timezone || 'UTC';
    }
  }

  /**
   * Start monitoring power schedule changes
   */
  startPowerScheduleMonitor(callback: (status: PowerScheduleStatus) => void): void {
    // Clear any existing interval to prevent leaks
    if (this.powerScheduleCheckInterval) {
      clearInterval(this.powerScheduleCheckInterval);
    }

    this.onPowerScheduleChange = callback;

    // Check immediately
    callback(this.getPowerScheduleStatus());

    // Check every minute
    this.powerScheduleCheckInterval = setInterval(() => {
      if (this.onPowerScheduleChange) {
        this.onPowerScheduleChange(this.getPowerScheduleStatus());
      }
    }, 60000); // Check every minute
  }

  /**
   * Stop monitoring power schedule
   */
  stopPowerScheduleMonitor(): void {
    if (this.powerScheduleCheckInterval) {
      clearInterval(this.powerScheduleCheckInterval);
      this.powerScheduleCheckInterval = null;
    }
    this.onPowerScheduleChange = null;
  }

  /**
   * Check if the screen should be powered on based on power schedule
   */
  getPowerScheduleStatus(): PowerScheduleStatus {
    if (!this.manifest?.powerSchedule) {
      return { shouldBeOn: true, isWithinWorkingHours: true, nextPowerChange: null, reason: 'no_schedule' };
    }

    const ps = this.manifest.powerSchedule;
    const now = new Date();
    const currentTime = this.getCurrentTimeInTimezone(now);
    const currentDay = this.getCurrentDayInTimezone(now);
    const currentDate = this.getCurrentDateInTimezone(now);

    // Check holiday exceptions FIRST (highest priority)
    if (ps.exceptions && ps.exceptions.length > 0) {
      const todayException = ps.exceptions.find((ex) => ex.date === currentDate);
      if (todayException) {
        // If screenOff is true and allDay is true, screen should be off all day
        if (todayException.screenOff && todayException.allDay) {
          console.log(`[Scheduler] Holiday exception: ${todayException.name} - screen off all day`);
          return {
            shouldBeOn: false,
            isWithinWorkingHours: false,
            nextPowerChange: this.getNextDayStart(),
            reason: 'power_off',
          };
        }

        // If custom hours for this holiday
        if (!todayException.allDay && todayException.powerOnTime && todayException.powerOffTime) {
          const isWithinHolidayHours = this.isTimeInRange(
            currentTime,
            todayException.powerOnTime,
            todayException.powerOffTime
          );
          if (!isWithinHolidayHours) {
            console.log(`[Scheduler] Holiday exception: ${todayException.name} - outside custom hours`);
            return {
              shouldBeOn: false,
              isWithinWorkingHours: false,
              nextPowerChange: this.getNextTimeChange(todayException.powerOnTime, todayException.powerOffTime),
              reason: 'power_off',
            };
          }
          // Within holiday hours, continue as normal
          console.log(`[Scheduler] Holiday exception: ${todayException.name} - within custom hours`);
        }

        // If screenOff is false, the holiday has normal operation (skip other checks)
        if (!todayException.screenOff) {
          console.log(`[Scheduler] Holiday exception: ${todayException.name} - normal operation`);
          return { shouldBeOn: true, isWithinWorkingHours: true, nextPowerChange: null, reason: 'active' };
        }
      }
    }

    // Check power on/off schedule (takes priority over working hours)
    if (ps.enabled && ps.powerOnTime && ps.powerOffTime) {
      const isWithinPowerSchedule = this.isTimeInRange(currentTime, ps.powerOnTime, ps.powerOffTime);
      if (!isWithinPowerSchedule) {
        return {
          shouldBeOn: false,
          isWithinWorkingHours: false,
          nextPowerChange: this.getNextTimeChange(ps.powerOnTime, ps.powerOffTime),
          reason: 'power_off',
        };
      }
    }

    // Check working hours (content schedule)
    if (ps.workingHoursEnabled && ps.workingHoursStart && ps.workingHoursEnd) {
      const workingDays = ps.workingDays || [1, 2, 3, 4, 5]; // Default to weekdays
      const isWorkingDay = workingDays.includes(currentDay);
      const isWithinWorkingHours = this.isTimeInRange(currentTime, ps.workingHoursStart, ps.workingHoursEnd);

      if (!isWorkingDay || !isWithinWorkingHours) {
        return {
          shouldBeOn: true, // Screen stays on but may show idle content
          isWithinWorkingHours: false,
          nextPowerChange: this.getNextTimeChange(ps.workingHoursStart, ps.workingHoursEnd),
          reason: 'outside_working_hours',
        };
      }
    }

    return { shouldBeOn: true, isWithinWorkingHours: true, nextPowerChange: null, reason: 'active' };
  }

  /**
   * Get current date as YYYY-MM-DD string in the configured timezone
   */
  private getCurrentDateInTimezone(date: Date): string {
    try {
      const dateStr = date.toLocaleDateString('en-CA', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        timeZone: this.timezone,
      });
      return dateStr; // Returns YYYY-MM-DD format
    } catch {
      // Fallback if timezone is invalid
      return date.toISOString().split('T')[0];
    }
  }

  /**
   * Get start of next day for holiday exception
   */
  private getNextDayStart(): Date {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    return tomorrow;
  }

  /**
   * Get current time as HH:MM string in the configured timezone
   */
  private getCurrentTimeInTimezone(date: Date): string {
    try {
      const timeStr = date.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: this.timezone,
      });
      return timeStr;
    } catch {
      // Fallback if timezone is invalid
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${hours}:${minutes}`;
    }
  }

  /**
   * Get current day of week (0=Sun, 1=Mon, ..., 6=Sat) in the configured timezone
   */
  private getCurrentDayInTimezone(date: Date): number {
    try {
      const dayStr = date.toLocaleDateString('en-US', {
        weekday: 'short',
        timeZone: this.timezone,
      });
      const dayMap: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
      return dayMap[dayStr] ?? date.getDay();
    } catch {
      return date.getDay();
    }
  }

  /**
   * Check if current time is within a time range (handles overnight ranges)
   */
  private isTimeInRange(current: string, start: string, end: string): boolean {
    const [currentH, currentM] = current.split(':').map(Number);
    const [startH, startM] = start.split(':').map(Number);
    const [endH, endM] = end.split(':').map(Number);

    const currentMinutes = currentH * 60 + currentM;
    const startMinutes = startH * 60 + startM;
    const endMinutes = endH * 60 + endM;

    if (startMinutes <= endMinutes) {
      // Normal range (e.g., 08:00 - 22:00)
      return currentMinutes >= startMinutes && currentMinutes < endMinutes;
    } else {
      // Overnight range (e.g., 22:00 - 06:00)
      return currentMinutes >= startMinutes || currentMinutes < endMinutes;
    }
  }

  /**
   * Calculate next power change time
   */
  private getNextTimeChange(startTime: string, endTime: string): Date | null {
    const now = new Date();
    const currentTime = this.getCurrentTimeInTimezone(now);

    const [currentH, currentM] = currentTime.split(':').map(Number);
    const [startH, startM] = startTime.split(':').map(Number);
    const [endH, endM] = endTime.split(':').map(Number);

    const currentMinutes = currentH * 60 + currentM;
    const startMinutes = startH * 60 + startM;
    const endMinutes = endH * 60 + endM;

    let nextChangeMinutes: number;
    if (currentMinutes < startMinutes) {
      nextChangeMinutes = startMinutes;
    } else if (currentMinutes < endMinutes) {
      nextChangeMinutes = endMinutes;
    } else {
      nextChangeMinutes = startMinutes + 24 * 60; // Tomorrow's start
    }

    const nextChange = new Date(now);
    const diffMinutes = nextChangeMinutes - currentMinutes;
    nextChange.setMinutes(nextChange.getMinutes() + diffMinutes);

    return nextChange;
  }

  /**
   * Start monitoring schedule changes (for blackout mode)
   */
  startScheduleMonitor(callback: (status: ScheduleStatus) => void): void {
    // Clear any existing interval to prevent leaks
    if (this.scheduleCheckInterval) {
      clearInterval(this.scheduleCheckInterval);
    }

    this.onScheduleChange = callback;

    // Check immediately
    const status = this.getScheduleStatus();
    this.lastScheduleStatus = status;
    callback(status);

    // Check every minute
    this.scheduleCheckInterval = setInterval(() => {
      if (this.onScheduleChange) {
        const newStatus = this.getScheduleStatus();

        // Only callback if status changed
        if (
          !this.lastScheduleStatus ||
          this.lastScheduleStatus.inSchedule !== newStatus.inSchedule ||
          this.lastScheduleStatus.activePlaylistId !== newStatus.activePlaylistId
        ) {
          console.log('[Scheduler] Schedule status changed:', newStatus);
          this.lastScheduleStatus = newStatus;
          this.onScheduleChange(newStatus);
        }
      }
    }, 60000); // Check every minute
  }

  /**
   * Stop monitoring schedule
   */
  stopScheduleMonitor(): void {
    if (this.scheduleCheckInterval) {
      clearInterval(this.scheduleCheckInterval);
      this.scheduleCheckInterval = null;
    }
    this.onScheduleChange = null;
    this.lastScheduleStatus = null;
  }

  /**
   * Get current schedule status by evaluating rules in real-time
   * This determines if we're within scheduled hours or should be in blackout
   */
  getScheduleStatus(): ScheduleStatus {
    if (!this.manifest?.schedule?.rules || this.manifest.schedule.rules.length === 0) {
      // No schedule rules - always active, use activePlaylistId from manifest
      return {
        inSchedule: true,
        activePlaylistId: this.manifest?.schedule?.activePlaylistId || null,
        currentRule: null,
      };
    }

    const now = new Date();
    const currentTime = this.getCurrentTimeInTimezone(now);
    const currentDay = this.getCurrentDayInTimezone(now);

    console.log('[Scheduler] Checking schedule:', {
      currentTime,
      currentDay,
      rulesCount: this.manifest.schedule.rules.length,
    });

    // Sort rules by priority (descending) so highest priority is checked first
    const sortedRules = [...this.manifest.schedule.rules].sort(
      (a, b) => (b.priority ?? 0) - (a.priority ?? 0)
    );

    // First pass: check interrupt rules (highest priority override)
    for (const rule of sortedRules) {
      if (!rule.isInterrupt) continue;

      const ruleDays = this.getDaysFromRule(rule);
      if (!ruleDays.includes(currentDay)) continue;

      if (rule.startTime && rule.endTime) {
        const isInRange = this.isTimeInRange(currentTime, rule.startTime, rule.endTime);
        if (isInRange) {
          console.log('[Scheduler] Interrupt rule matched:', { ruleId: rule.id, priority: rule.priority });
          return {
            inSchedule: true,
            activePlaylistId: rule.playlistId || this.manifest.schedule.activePlaylistId || null,
            currentRule: {
              ruleId: rule.id,
              startTime: rule.startTime,
              endTime: rule.endTime,
              days: ruleDays,
            },
          };
        }
      }
    }

    // Second pass: evaluate normal rules by priority
    for (const rule of sortedRules) {
      if (rule.isInterrupt) continue;

      const ruleDays = this.getDaysFromRule(rule);
      if (!ruleDays.includes(currentDay)) {
        continue;
      }

      if (rule.startTime && rule.endTime) {
        const isInRange = this.isTimeInRange(currentTime, rule.startTime, rule.endTime);
        console.log('[Scheduler] Time check:', {
          ruleId: rule.id,
          currentTime,
          startTime: rule.startTime,
          endTime: rule.endTime,
          priority: rule.priority,
          isInRange,
        });

        if (isInRange) {
          return {
            inSchedule: true,
            activePlaylistId: rule.playlistId || this.manifest.schedule.activePlaylistId || null,
            currentRule: {
              ruleId: rule.id,
              startTime: rule.startTime,
              endTime: rule.endTime,
              days: ruleDays,
            },
          };
        }
      }
    }

    // No rules matched - we're in blackout
    console.log('[Scheduler] No matching rules - entering blackout');
    return {
      inSchedule: false,
      activePlaylistId: null,
      currentRule: null,
    };
  }

  /**
   * Extract days array from a schedule rule
   * Days are 0=Sunday, 1=Monday, ..., 6=Saturday
   */
  private getDaysFromRule(rule: { rrule?: string; days?: number[] }): number[] {
    // If explicit days array is provided, use it
    if (rule.days && rule.days.length > 0) {
      return rule.days;
    }

    // Parse from rrule if available
    if (rule.rrule) {
      return this.getDaysFromRRule(rule.rrule);
    }

    // Default to all days
    return [0, 1, 2, 3, 4, 5, 6];
  }

  /**
   * Parse RRULE string to extract BYDAY (days of week)
   */
  private getDaysFromRRule(rrule: string): number[] {
    // Extract BYDAY from RRULE (e.g., "FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR")
    const bydayMatch = rrule.match(/BYDAY=([^;]+)/i);
    if (!bydayMatch) {
      return [0, 1, 2, 3, 4, 5, 6]; // All days if no BYDAY
    }

    const dayMap: Record<string, number> = {
      SU: 0,
      MO: 1,
      TU: 2,
      WE: 3,
      TH: 4,
      FR: 5,
      SA: 6,
    };

    const dayStrs = bydayMatch[1].split(',');
    const days = dayStrs
      .map((d) => dayMap[d.trim().toUpperCase()])
      .filter((d) => d !== undefined);

    return days.length > 0 ? days : [0, 1, 2, 3, 4, 5, 6];
  }

  /**
   * Get the currently active playlist using client-side rule evaluation.
   * Returns null during blackout (rules exist but none match) — caller should not play.
   */
  getActivePlaylist(): ActivePlaylist | null {
    if (!this.manifest) return null;

    // Use client-side schedule evaluation to determine active playlist.
    // When rules exist but none match (blackout), returns null — caller should not play content.
    const status = this.getScheduleStatus();
    const activePlaylistId = status.activePlaylistId;

    // No active playlist (blackout or no schedule)
    if (!activePlaylistId) return null;

    let playlist = this.manifest.playlists?.find((p) => p.id === activePlaylistId) ?? null;

    if (!playlist) {
      // Playlist ID exists but not found in manifest — fallback to first playlist
      const fallback = this.manifest.playlists?.[0];
      if (!fallback) return null;

      return {
        id: fallback.id,
        items: fallback.items,
        assets: this.buildAssetMap(),
        mode: fallback.mode ?? 'sequential',
      };
    }

    return {
      id: playlist.id,
      items: playlist.items,
      assets: this.buildAssetMap(),
      mode: playlist.mode ?? 'sequential',
    };
  }

  /**
   * Build a map of asset ID to asset data for quick lookup
   */
  private buildAssetMap(): Map<string, ManifestAsset> {
    const map = new Map<string, ManifestAsset>();
    if (this.manifest?.assets) {
      for (const asset of this.manifest.assets) {
        map.set(asset.id, asset);
      }
    }
    return map;
  }

  /**
   * Get the next schedule change time (for UI countdown, etc.)
   * Note: With server-computed schedules, this is approximated
   */
  getNextScheduleChange(): Date | null {
    // For now, return null - schedule changes are driven by manifest updates
    return null;
  }

  /**
   * Format time for display
   */
  formatTime(date: Date): string {
    return date.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: this.timezone,
    });
  }
}

export const scheduler = new Scheduler();
